import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { ICommodity } from '../commodity/commodity';
import { IOptionType } from '@zz2/zz2-ui';
import { IOrchard } from './orchard';
import { IOrchardFormValues } from './orchardFormValues';
import { IOrchardUpsert } from './orchardUpsert';
import { IVariety } from '../variety/variety';
import { IFarm } from '../farm/farm';
import { IDepartment } from '../department/department';
import { IProject } from '../project/project';
import OptionType from '../../optionType';
import moment from 'moment';
import { isNullOrWhiteSpace } from '../../../../services/appFunctionsService';

export default class OrchardModelHelper {
    public static createFormValues = (
        orchard : IOrchard | null,
        defaultCommodity : ICommodity | null,
        commodities : Array<ICommodity>,
        varieties : Array<IVariety>,
        farms : Array<IFarm>,
        departments : Array<IDepartment>,
        projects : Array<IProject>,
    ) : IOrchardFormValues => {
        if (!orchard) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            name: '',
            subBlock: '',
            complianceCode: '',
            plantedHectares: null,
            commodity: defaultCommodity
                ? OptionType.fromDataModel(defaultCommodity)
                : null,
            defaultVariety: null,
            farm: null,
            department: null,
            project: null,
            endedOn: null,
            isActive: true,
        };

        const commodity = commodities.find(x => x.id === orchard.commodityId);
        const variety = varieties.find(x => x.id === orchard.defaultVarietyId);
        const farm = farms.find(x => x.id === orchard.farmId);
        const department = departments.find(x => x.id === orchard.departmentId);
        const project = projects.find(x => x.id === orchard.projectId);

        return {
            id: orchard.id,
            guid: orchard.guid,
            code: orchard.code,
            name: orchard.name ?? '',
            subBlock: orchard.subBlock,
            complianceCode: orchard.complianceCode,
            plantedHectares: orchard.plantedHectares,
            commodity: commodity
                ? OptionType.fromDataModel(commodity)
                : null,
            defaultVariety: variety
                ? OptionType.fromDataModel(variety)
                : null,
            farm: farm
                ? { // TODO: OptionType.FromDataModel
                    value: farm.id,
                    label: `(${farm.code}) - ${farm.name}`,
                }
                : null,
            department: department
                ? { // TODO: OptionType.FromDataModel
                    value: department.id,
                    label: `(${department.shortDescription}) - ${department.description}`,
                }
                : null,
            project: project
                ? { // TODO: OptionType.FromDataModel
                    value: project.id,
                    label: `(${project.number}) - ${project.description}`,
                }
                : null,
            endedOn: orchard.endedOn ? moment.utc(orchard.endedOn).local() : null,
            isActive: orchard.isActive,
        };
    };

    public static createUpsert = (formValues : IOrchardFormValues) : IOrchardUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            name: formValues.name,
            subBlock: isNullOrWhiteSpace(formValues.subBlock) ? null : formValues.subBlock,
            complianceCode: isNullOrWhiteSpace(formValues.complianceCode) ? null : formValues.complianceCode,
            plantedHectares: formValues.plantedHectares,
            commodityId: Number(formValues.commodity?.value ?? 0),
            defaultVarietyId: Number(formValues.defaultVariety?.value ?? 0),
            farmId: Number(formValues.farm?.value ?? 0),
            departmentId: formValues.department
                ? Number(formValues.department.value)
                : null,
            projectId: formValues.project
                ? Number(formValues.project.value)
                : null,
            endedOn: formValues.endedOn,
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IOrchardFormValues> => Yup.object({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        name: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        subBlock: Yup.string().trim().defined().nullable()
            .transform((value, originalValue) => 
                isNullOrWhiteSpace(originalValue) ? null : value
            ),
        complianceCode: Yup.string().trim().defined().nullable()
            .transform((value, originalValue) => 
                isNullOrWhiteSpace(originalValue) ? null : value
            ),
        plantedHectares: Yup.number().typeError('Please enter a number.').defined().nullable(),
        commodity: Yup.mixed<IOptionType>().defined().nullable().required('Required'),
        defaultVariety: Yup.mixed<IOptionType>().defined().nullable().required('Required'),
        farm: Yup.mixed<IOptionType>().defined().nullable().required('Required'),
        department: Yup.mixed<IOptionType>().defined().nullable(),
        project: Yup.mixed<IOptionType>().defined().nullable(),
        endedOn: Yup.mixed<moment.Moment>().defined().nullable(),
        isActive: Yup.boolean().required('Required'),
    });
}
